import { Injectable } from '@angular/core';
import { BaseWooCommerceV2ApiService } from './base-woo-commerce-v2-api.service';
import { map, Observable } from 'rxjs';
import { WooCommerceCheckoutResponse } from '../../../interfaces/woo-commerce/api/checkout/checkout-api.interface';
import { mapHappyCheckoutToWooCheckout } from '../../happy/happy-api-mappers';
import { generateUUID } from '../../../../shared/utils/generate-uuid.util';
import {
  HappyCheckoutRequest,
  HappyFrequencyUnit,
  HappyOrderProcessingType,
  HappyPaymentGateway
} from '../../../interfaces/happy-api/happy-api.interface';

export interface CheckoutOrderParams {
  gateway: HappyPaymentGateway;
  paymentMethod?: string;
  paymentTokenId?: number;
  paymentMethodData?: Record<string, unknown>;

  isSubscription?: boolean;
  frequencyValue?: number;
  frequencyUnit?: HappyFrequencyUnit;

  orderProcessingType?: HappyOrderProcessingType;

  /** True when total === 0 (gift card / points / coupons cover full amount). */
  isZeroTotal?: boolean;
}

@Injectable({ providedIn: 'root' })
export class WooCommerceCheckoutApiService extends BaseWooCommerceV2ApiService {
  private readonly happyCheckoutUrl: string = '/cart/checkout';

  /**
   * POST /cart/checkout
   *
   * Per Cart-Subs.md §11:
   *  - Zero-total: body = {} (no gateway needed)
   *  - Saved card: { gateway, payment_method, payment_token_id }
   *  - New Stripe: { gateway, payment_method, payment_method_data: { payment_method: "pm_xxx" } }
   *  - New AuthNet: { gateway, payment_method, payment_method_data: { card_number, expiration_date, card_code } }
   *  - PayPal:     { gateway: "paypal", payment_method: "digital_wallet", payment_method_data: {} }
   *
   * Subscription checkout adds: is_subscription, frequency_value, frequency_unit
   */
  createOrder(
    params: CheckoutOrderParams
  ): Observable<WooCommerceCheckoutResponse> {

    if (params.isZeroTotal) {
      const zeroBody: Record<string, unknown> = {};
      if (params.orderProcessingType) {
        zeroBody['order_processing_type'] = params.orderProcessingType;
      }
      return this.post(this.happyCheckoutUrl, zeroBody, {
        headers: { 'Idempotency-Key': `checkout-${generateUUID()}` }
      }).pipe(map((response: any) => mapHappyCheckoutToWooCheckout(response)));
    }

    const payload: HappyCheckoutRequest = {
      gateway: params.gateway,
      payment_gateway: params.gateway,
      payment_method: params.paymentMethod || 'credit_card'
    };

    if (params.paymentTokenId != null) {
      payload.payment_token_id = params.paymentTokenId;
    } else if (params.paymentMethodData) {
      payload.payment_method_data = params.paymentMethodData;
    }

    if (params.isSubscription) {
      payload.is_subscription = true;
      if (params.frequencyValue != null) {
        payload.frequency_value = params.frequencyValue;
      }
      if (params.frequencyUnit) {
        payload.frequency_unit = params.frequencyUnit;
      }
    }

    if (params.orderProcessingType) {
      payload.order_processing_type = params.orderProcessingType;
    }

    return this.post(this.happyCheckoutUrl, payload, {
      headers: {
        'Idempotency-Key': `checkout-${generateUUID()}`
      }
    }).pipe(map((response: any) => mapHappyCheckoutToWooCheckout(response)));
  }
}
